// src/components/profile/SectionAlignment.jsx
import React, { useState, useMemo } from 'react';
import { Target, TrendingUp, CheckCircle, AlertTriangle, XCircle, Radar, Download, Share2, Award, BarChart3, Zap } from 'lucide-react';

const AlignmentBar = ({ label, score, description }) => {
  const getColorClass = (score) => {
    if (score >= 80) return { bg: 'bg-green-500', text: 'text-green-700', icon: CheckCircle };
    if (score >= 60) return { bg: 'bg-yellow-500', text: 'text-yellow-700', icon: AlertTriangle };
    return { bg: 'bg-red-500', text: 'text-red-700', icon: XCircle };
  };

  const colorConfig = getColorClass(score);
  const Icon = colorConfig.icon;

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${colorConfig.text}`} />
          <span className="text-sm font-medium text-gray-900">{label}</span>
        </div>
        <span className={`text-sm font-bold ${colorConfig.text}`}>{score}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
        <div 
          className={`h-2 rounded-full transition-all duration-500 ${colorConfig.bg}`}
          style={{ width: `${Math.min(100, Math.max(0, score))}%` }}
        />
      </div>
      <div className="text-xs text-gray-600">{description}</div>
    </div>
  );
};

const RadarChart = ({ dimensions }) => {
  const size = 200;
  const center = size / 2;
  const maxRadius = 80;
  
  // Create pentagon points for 5 dimensions
  const angleStep = (2 * Math.PI) / dimensions.length;
  
  const getPoint = (index, radius) => {
    const angle = (index * angleStep) - (Math.PI / 2); // Start from top
    return {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle)
    };
  };

  // Grid circles (20%, 40%, 60%, 80%, 100%)
  const gridLevels = [0.2, 0.4, 0.6, 0.8, 1.0];
  
  // Data points based on scores
  const dataPoints = dimensions.map((dim, index) => {
    const radius = (dim.score / 100) * maxRadius;
    return getPoint(index, radius);
  });

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} className="mb-4">
        {/* Grid circles */}
        {gridLevels.map((level, idx) => (
          <circle
            key={idx}
            cx={center}
            cy={center}
            r={maxRadius * level}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="1"
          />
        ))}
        
        {/* Grid lines */}
        {dimensions.map((_, index) => {
          const point = getPoint(index, maxRadius);
          return (
            <line
              key={index}
              x1={center}
              y1={center}
              x2={point.x}
              y2={point.y}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          );
        })}
        
        {/* Data polygon */}
        <polygon
          points={dataPoints.map(p => `${p.x},${p.y}`).join(' ')}
          fill="#18365E"
          fillOpacity="0.3"
          stroke="#18365E"
          strokeWidth="2"
        />
        
        {/* Data points */}
        {dataPoints.map((point, index) => (
          <circle
            key={index}
            cx={point.x}
            cy={point.y}
            r="4"
            fill="#FF6E4C"
            stroke="white"
            strokeWidth="2"
          />
        ))}
        
        {/* Labels */}
        {dimensions.map((dim, index) => {
          const labelPoint = getPoint(index, maxRadius + 20);
          return (
            <text
              key={index}
              x={labelPoint.x}
              y={labelPoint.y}
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-xs font-medium fill-gray-700"
            >
              {dim.label}
            </text>
          );
        })}
      </svg>
      
      {/* Legend */}
      <div className="text-center">
        <div className="text-sm font-medium text-gray-900 mb-1">Overall Alignment Score</div>
        <div className="text-2xl font-bold text-[#18365E]">
          {Math.round(dimensions.reduce((sum, dim) => sum + dim.score, 0) / dimensions.length)}%
        </div>
      </div>
    </div>
  );
};

export default function SectionAlignment({ 
  profileData = {}, 
  alignmentData,  
  clientId,
  useCaseId,
  isCustomized = false
}) {
  const [showMethodNotes, setShowMethodNotes] = useState({});
  const [dataSource, setDataSource] = useState('checking'); // Track data source
  
  // Extract overall score from profileData - FROM API
  const overallScore = profileData?.strategic_alignment_score 
    ? profileData.strategic_alignment_score * 100  // Convert decimal to percentage
    : 85; // Fallback
  
  // Process alignment data from props
  const alignmentItems = useMemo(() => {
    // Handle both array and object response formats
    const items = Array.isArray(alignmentData) ? alignmentData : 
                  alignmentData?.priorities || alignmentData?.data || alignmentData?.items || [];
    
    if (items.length > 0) {
      console.log('✅ Using REAL alignment data from API');
      return items.map(item => ({
        priority: item.priority,
        score: item.score,
        narrative: item.narrative,
        method_note: item.method_note,
        category_id: item.category_id || 1,
        weight: item.weight || 0.25,
        dataSource: 'API' // Mark as from API
      }));
    }
    
    // FALLBACK DATA - MOCKED
    console.warn('⚠️ Using FALLBACK alignment data (API data not available)');
    return [
      { 
        priority: 'DEBUG: Value-Based Care (MOCK)', 
        score: 92, 
        narrative: 'MOCKED: Strong alignment with VBC initiatives and payer contracts',
        method_note: 'MOCKED: Scored based on direct support for 3 major VBC contracts',
        category_id: 1,
        weight: 0.3,
        dataSource: 'MOCK'
      },
      { 
        priority: 'DEBUG: Population Health (MOCK)', 
        score: 78, 
        narrative: 'MOCKED: Moderate support for population health management goals',
        method_note: 'MOCKED: Assessment based on patient outcome improvements',
        category_id: 2,
        weight: 0.25,
        dataSource: 'MOCK'
      },
      { 
        priority: 'DEBUG: Digital Transformation (MOCK)', 
        score: 85, 
        narrative: 'MOCKED: Good fit with digital strategy and technology roadmap',
        method_note: 'MOCKED: Evaluated against digital maturity framework',
        category_id: 3,
        weight: 0.2,
        dataSource: 'MOCK'
      },
      { 
        priority: 'DEBUG: Cost Optimization (MOCK)', 
        score: 95, 
        narrative: 'MOCKED: Excellent cost reduction and efficiency potential',
        method_note: 'MOCKED: Based on projected savings analysis',
        category_id: 4,
        weight: 0.25,
        dataSource: 'MOCK'
      }
    ];
  }, [alignmentData]);

  // Client weights - FROM API OR DEFAULTS
  const clientWeights = useMemo(() => {
    if (alignmentData?.weights && alignmentData.weights.length > 0) {
      console.log('✅ Using REAL weights from API');
      return alignmentData.weights;
    }
    // DEFAULT WEIGHTS - MOCKED
    console.warn('⚠️ Using DEFAULT weights (not from API)');
    return [
      { category_id: 1, weight: 0.3, label: 'Value-Based Care (DEFAULT)' },
      { category_id: 4, weight: 0.25, label: 'Cost Optimization (DEFAULT)' },
      { category_id: 3, weight: 0.2, label: 'Digital Transformation (DEFAULT)' },
      { category_id: 2, weight: 0.25, label: 'Population Health (DEFAULT)' }
    ];
  }, [alignmentData]);

  // Get top 2 weighted priorities for snapshot
  const topPriorities = useMemo(() => {
    return clientWeights
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 2);
  }, [clientWeights]);

  // TREND DATA - COMPLETELY MOCKED
  const trendData = useMemo(() => {
    console.warn('⚠️ Trend data is MOCKED - not from API');
    return [
      { month: 'Jan (MOCK)', score: 78 },
      { month: 'Feb (MOCK)', score: 82 },
      { month: 'Mar (MOCK)', score: 85 },
      { month: 'Apr (MOCK)', score: 87 },
      { month: 'May (MOCK)', score: overallScore }
    ];
  }, [overallScore]);

  // Get top 3 weighted priorities for rationale cards
  const topRationaleItems = useMemo(() => {
    return alignmentItems
      .map(item => ({
        ...item,
        weight: clientWeights.find(w => w.category_id === item.category_id)?.weight || item.weight
      }))
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 3);
  }, [alignmentItems, clientWeights]);

  // Strategic goals and KPIs - FROM API OR DEFAULTS
  const strategicGoals = useMemo(() => {
    if (alignmentData?.goals && alignmentData.goals.length > 0) {
      console.log('✅ Using REAL goals from API');
      return alignmentData.goals;
    }
    console.warn('⚠️ Using DEFAULT goals (not from API)');
    return [
      'DEBUG: Value-Based Care Excellence (DEFAULT)',
      'DEBUG: Population Health Leadership (DEFAULT)', 
      'DEBUG: Digital Innovation (DEFAULT)',
      'DEBUG: Financial Sustainability (DEFAULT)'
    ];
  }, [alignmentData]);

  const linkedKPIs = useMemo(() => {
    if (alignmentData?.kpis && alignmentData.kpis.length > 0) {
      console.log('✅ Using REAL KPIs from API');
      return alignmentData.kpis;
    }
    console.warn('⚠️ Using DEFAULT KPIs (not from API)');
    return [
      'DEBUG: Patient Satisfaction Score (DEFAULT)',
      'DEBUG: Cost per Episode (DEFAULT)',
      'DEBUG: Care Gap Closure Rate (DEFAULT)',
      'DEBUG: Digital Adoption Rate (DEFAULT)'
    ];
  }, [alignmentData]);

  // RADAR CHART DIMENSIONS - COMPUTED BUT USES HARDCODED MAPPING
  const radarDimensions = useMemo(() => {
    console.warn('⚠️ Radar dimensions use HARDCODED category mapping');
    const dimensionMapping = {
      'Strategic': [1, 2],
      'Financial': [4],
      'Operational': [2, 3],
      'Clinical': [1, 2],
      'Innovation': [3]
    };

    return Object.entries(dimensionMapping).map(([label, categoryIds]) => {
      const relevantItems = alignmentItems.filter(item => 
        categoryIds.includes(item.category_id)
      );
      const avgScore = relevantItems.length > 0 
        ? relevantItems.reduce((sum, item) => sum + item.score, 0) / relevantItems.length
        : 0;
      return { 
        label: label + (relevantItems[0]?.dataSource === 'MOCK' ? ' (MOCK)' : ''), 
        score: avgScore 
      };
    });
  }, [alignmentItems]);

  // Strategic proof synthesis
  const strategicProofSentence = useMemo(() => {
    const strongPillars = topRationaleItems.filter(item => item.score >= 80);
    const topPillar = topRationaleItems[0];
    const dataNote = topPillar?.dataSource === 'MOCK' ? ' [MOCKED DATA]' : '';
    return `This use case advances ${strongPillars.length}/${alignmentItems.length} strategic pillars with strongest lift in ${topPillar?.priority || 'Strategic Priorities'}${dataNote}.`;
  }, [topRationaleItems, alignmentItems]);

  // Export functions
  const handleExportPDF = () => {
    console.log('Export Alignment Brief PDF - functionality to be implemented');
  };

  const handleExportCSV = () => {
    const csvContent = [
      ['Priority', 'Score', 'Client Weight', 'Narrative', 'Method Note', 'Data Source'],
      ...alignmentItems.map(item => {
        const weight = clientWeights.find(w => w.category_id === item.category_id)?.weight || item.weight;
        return [
          item.priority,
          item.score,
          weight,
          item.narrative,
          item.method_note || '',
          item.dataSource || 'Unknown'
        ];
      })
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `alignment-analysis-${useCaseId || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleMethodNote = (index) => {
    setShowMethodNotes(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Check data sources on mount
  React.useEffect(() => {
    const hasRealData = alignmentData?.priorities?.length > 0 || 
                       (Array.isArray(alignmentData) && alignmentData.length > 0);
    const hasRealWeights = alignmentData?.weights?.length > 0;
    const hasRealGoals = alignmentData?.goals?.length > 0;
    const hasRealKPIs = alignmentData?.kpis?.length > 0;
    
    console.log('=== ALIGNMENT DATA SOURCE CHECK ===');
    console.log('Priorities:', hasRealData ? '✅ API' : '❌ MOCK');
    console.log('Weights:', hasRealWeights ? '✅ API' : '❌ DEFAULT');
    console.log('Goals:', hasRealGoals ? '✅ API' : '❌ DEFAULT');
    console.log('KPIs:', hasRealKPIs ? '✅ API' : '❌ DEFAULT');
    console.log('Trend Data: ❌ ALWAYS MOCKED');
    console.log('Radar Mapping: ❌ HARDCODED');
    console.log('===================================');
  }, [alignmentData]);

  return (
    <section id="strategic-alignment" aria-labelledby="alignment-title" className="scroll-mt-24">
      {/* Debug Banner - Remove in production */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
        <div className="text-sm font-medium text-yellow-800 mb-2">🔍 Data Source Debug Info:</div>
        <div className="grid grid-cols-2 gap-2 text-xs text-yellow-700">
          <div>• Alignment Items: {alignmentItems[0]?.dataSource || 'Unknown'}</div>
          <div>• Weights: {alignmentData?.weights ? 'API' : 'DEFAULT'}</div>
          <div>• Goals: {alignmentData?.goals ? 'API' : 'DEFAULT'}</div>
          <div>• KPIs: {alignmentData?.kpis ? 'API' : 'DEFAULT'}</div>
          <div>• Trend Data: ALWAYS MOCKED</div>
          <div>• Overall Score: {profileData?.strategic_alignment_score ? 'API' : 'FALLBACK'}</div>
        </div>
      </div>

      {/* Alignment Snapshot Strip */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 id="alignment-title" className="text-xl font-bold text-[#18365E] flex items-center gap-2">
            <Target className="w-5 h-5" />
            Strategic Alignment
          </h2>
          <div className="flex items-center gap-2">
            {isCustomized && (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 border border-purple-200">
                ✨ Customized
              </span>
            )}
            <button 
              onClick={handleExportPDF}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-[#18365E] text-white rounded-lg hover:bg-[#18365E]/90 transition-colors"
            >
              <Download className="w-4 h-4" />
              Alignment Brief
            </button>
            <button 
              onClick={handleExportCSV}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              CSV
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Overall Alignment Score */}
          <div className="text-center">
            <div className={`text-4xl font-bold mb-2 ${
              overallScore >= 80 ? 'text-green-600' :
              overallScore >= 60 ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {Math.round(overallScore)}%
            </div>
            <div className={`text-sm font-medium mb-1 ${
              overallScore >= 80 ? 'text-green-700' :
              overallScore >= 60 ? 'text-yellow-700' : 'text-red-700'
            }`}>
              {overallScore >= 80 ? 'Strong' : overallScore >= 60 ? 'Moderate' : 'Weak'} Alignment
            </div>
            <div className="text-xs text-gray-500">
              Overall Strategic Score {profileData?.strategic_alignment_score ? '(from API)' : '(fallback)'}
            </div>
            
            {/* Trend Sparkline - MOCKED */}
            <div className="mt-3">
              <div className="text-xs text-gray-600 mb-1">Trend (MOCKED)</div>
              <svg className="w-full h-8" viewBox="0 0 100 20">
                <path
                  d={`M 0,${20 - (trendData[0].score / 100 * 15)} ${trendData.map((d, i) => 
                    `L ${(i / (trendData.length - 1)) * 100},${20 - (d.score / 100 * 15)}`
                  ).join(' ')}`}
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="2"
                />
              </svg>
            </div>
          </div>
          
          {/* Top Weighted Priorities */}
          <div className="lg:col-span-2">
            <div className="text-sm font-medium text-gray-700 mb-3">Top Weighted Priorities</div>
            <div className="flex flex-wrap gap-3 mb-4">
              {topPriorities.map((priority, idx) => {
                const item = alignmentItems.find(item => item.category_id === priority.category_id);
                return (
                  <div key={idx} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg">
                    <Award className="w-4 h-4 text-[#18365E]" />
                    <div className="text-sm">
                      <span className="font-medium text-[#18365E]">{priority.label}</span>
                      <span className="text-gray-600 ml-2">({Math.round(priority.weight * 100)}% weight)</span>
                    </div>
                    {item && (
                      <span className={`text-xs px-2 py-1 rounded font-medium ${
                        item.score >= 80 ? 'bg-green-100 text-green-800' :
                        item.score >= 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {item.score}%
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Quick Defense Answer */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                <div className="text-sm">
                  <span className="font-medium text-green-800">Strategic Defense:</span>
                  <span className="text-green-700 ml-1">
                    {strategicProofSentence}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Priority Breakdown Bars */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6">
        <h3 className="text-lg font-semibold text-[#18365E] mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Priority Breakdown
        </h3>
        
        <div className="space-y-4">
          {alignmentItems.map((item, idx) => {
            const weight = clientWeights.find(w => w.category_id === item.category_id)?.weight || item.weight;
            const isHighWeight = weight >= 0.25;
            
            return (
              <div key={idx} className="relative">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className={`flex items-center gap-1 ${
                      item.score >= 80 ? 'text-green-700' :
                      item.score >= 60 ? 'text-yellow-700' : 'text-red-700'
                    }`}>
                      {item.score >= 80 ? <CheckCircle className="w-4 h-4" /> :
                       item.score >= 60 ? <AlertTriangle className="w-4 h-4" /> :
                       <XCircle className="w-4 h-4" />}
                      <span className="text-sm font-medium text-gray-900">{item.priority}</span>
                    </div>
                    {isHighWeight && (
                      <div className="w-2 h-2 bg-[#FF6E4C] rounded-full" title="High client weight" />
                    )}
                    {item.dataSource === 'MOCK' && (
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">MOCK</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600">{Math.round(weight * 100)}% weight</span>
                    <span className={`text-sm font-bold ${
                      item.score >= 80 ? 'text-green-700' :
                      item.score >= 60 ? 'text-yellow-700' : 'text-red-700'
                    }`}>
                      {item.score}%
                    </span>
                  </div>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-3 mb-2 relative overflow-hidden">
                  <div 
                    className={`h-3 rounded-full transition-all duration-500 ${
                      item.score >= 80 ? 'bg-green-500' :
                      item.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                    } ${isHighWeight ? 'ring-2 ring-[#FF6E4C] ring-inset' : ''}`}
                    style={{ width: `${Math.min(100, Math.max(0, item.score))}%` }}
                  />
                </div>
                
                <div className="text-xs text-gray-600 mb-2">{item.narrative}</div>
                
                {item.method_note && (
                  <button
                    onClick={() => toggleMethodNote(idx)}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    {showMethodNotes[idx] ? 'Hide method' : 'Show method'}
                  </button>
                )}
                
                {showMethodNotes[idx] && item.method_note && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                    <span className="font-medium">Method:</span> {item.method_note}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Rationale Cards */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6">
        <h3 className="text-lg font-semibold text-[#18365E] mb-4">Top Priority Rationales</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {topRationaleItems.map((item, idx) => (
            <div key={idx} className={`p-4 rounded-xl border-l-4 ${
              item.score >= 80 ? 'border-green-500 bg-green-50' :
              item.score >= 60 ? 'border-yellow-500 bg-yellow-50' :
              'border-red-500 bg-red-50'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900 text-sm">{item.priority}</h4>
                <span className={`text-sm font-bold ${
                  item.score >= 80 ? 'text-green-700' :
                  item.score >= 60 ? 'text-yellow-700' : 'text-red-700'
                }`}>
                  {item.score}%
                </span>
              </div>
              <p className="text-sm text-gray-700 mb-2">{item.narrative}</p>
              <div className="flex items-center gap-2">
                <span className="text-xs bg-white px-2 py-1 rounded border text-gray-600">
                  {Math.round(item.weight * 100)}% weight
                </span>
                {item.dataSource === 'MOCK' && (
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">MOCK</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Radar Chart */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-[#18365E] mb-4 flex items-center gap-2">
            <Radar className="w-5 h-5" />
            Multi-Dimensional Alignment
          </h3>
          <div className="flex justify-center">
            <RadarChart dimensions={radarDimensions} />
          </div>
          <div className="text-xs text-center text-gray-500 mt-2">
            Note: Dimension mapping is hardcoded
          </div>
        </div>

        {/* Strategic Proof Panel */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-[#18365E] mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Strategic Proof
          </h3>
          
          {/* Linked Strategic Goals */}
          <div className="mb-4">
            <div className="text-sm font-medium text-gray-700 mb-2">
              Linked Organizational Goals {!alignmentData?.goals && '(DEFAULT)'}
            </div>
            <div className="flex flex-wrap gap-2">
              {strategicGoals.map((goal, idx) => (
                <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                  {goal}
                </span>
              ))}
            </div>
          </div>
          
          {/* Linked KPIs */}
          <div className="mb-4">
            <div className="text-sm font-medium text-gray-700 mb-2">
              Linked KPIs {!alignmentData?.kpis && '(DEFAULT)'}
            </div>
            <div className="flex flex-wrap gap-2">
              {linkedKPIs.map((kpi, idx) => (
                <span key={idx} className="px-3 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                  {kpi}
                </span>
              ))}
            </div>
          </div>
          
          {/* Strategic Synthesis */}
          <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Target className="w-4 h-4 text-green-600 mt-0.5" />
              <div className="text-sm">
                <span className="font-medium text-green-800">Strategic Synthesis:</span>
                <span className="text-green-700 ml-1">{strategicProofSentence}</span>
              </div>
            </div>
          </div>
          
          {/* Alignment Confidence */}
          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Alignment Confidence</span>
              <span className={`text-sm font-bold ${
                overallScore >= 80 ? 'text-green-600' :
                overallScore >= 60 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {overallScore >= 80 ? 'High' : overallScore >= 60 ? 'Medium' : 'Low'}
              </span>
            </div>
            <div className="text-xs text-gray-600 mt-1">
              Based on weighted priority analysis and strategic mapping
            </div>
          </div>
        </div>
      </div>

      {/* Strategic Alignment Score Gauge */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6">
        <h3 className="text-lg font-semibold text-[#18365E] mb-6 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Alignment Score Breakdown
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {radarDimensions.map((dimension, idx) => (
            <div key={idx} className="text-center">
              <div className="relative w-24 h-24 mx-auto mb-3">
                <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                  {/* Background circle */}
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    stroke="#e5e7eb"
                    strokeWidth="8"
                    fill="none"
                  />
                  {/* Progress circle */}
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    stroke={dimension.score >= 80 ? '#10b981' : dimension.score >= 60 ? '#f59e0b' : '#ef4444'}
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 40}`}
                    strokeDashoffset={`${2 * Math.PI * 40 * (1 - dimension.score / 100)}`}
                    className="transition-all duration-1000"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`text-lg font-bold ${
                    dimension.score >= 80 ? 'text-green-600' :
                    dimension.score >= 60 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {Math.round(dimension.score)}%
                  </span>
                </div>
              </div>
              <div className="text-sm font-medium text-gray-900">{dimension.label}</div>
              <div className={`text-xs mt-1 ${
                dimension.score >= 80 ? 'text-green-600' :
                dimension.score >= 60 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {dimension.score >= 80 ? 'Strong' : dimension.score >= 60 ? 'Moderate' : 'Weak'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Priority Matrix (Impact vs Effort) */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-[#18365E] mb-6 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Strategic Priority Matrix
        </h3>
        
        <div className="relative">
          {/* Matrix Grid */}
          <div className="w-full h-80 relative border-2 border-gray-300 rounded-lg bg-gradient-to-tr from-red-50 via-yellow-50 to-green-50">
            {/* Grid lines */}
            <div className="absolute inset-0">
              <div className="absolute top-1/2 left-0 right-0 h-px bg-gray-300"></div>
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300"></div>
            </div>
            
            {/* Quadrant labels */}
            <div className="absolute top-2 left-2 text-xs font-medium text-red-700">Low Impact, High Effort</div>
            <div className="absolute top-2 right-2 text-xs font-medium text-green-700">High Impact, High Effort</div>
            <div className="absolute bottom-2 left-2 text-xs font-medium text-yellow-700">Low Impact, Low Effort</div>
            <div className="absolute bottom-2 right-2 text-xs font-medium text-blue-700">High Impact, Low Effort</div>
            
            {/* Plot alignment items */}
            {alignmentItems.map((item, idx) => {
              // Map score to impact (higher score = higher impact)
              const impact = item.score;
              // Map weight to effort (higher weight = more effort to change)
              const effort = (clientWeights.find(w => w.category_id === item.category_id)?.weight || 0.25) * 400;
              
              const x = (impact / 100) * 100;
              const y = 100 - (effort / 100);
              
              return (
                <div
                  key={idx}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 group cursor-pointer"
                  style={{ left: `${x}%`, top: `${y}%` }}
                  title={`${item.priority}: ${item.score}% alignment`}
                >
                  <div className={`w-4 h-4 rounded-full border-2 border-white shadow-lg ${
                    item.score >= 80 ? 'bg-green-500' :
                    item.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                  } group-hover:scale-150 transition-transform`}>
                  </div>
                  <div className="absolute top-6 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                    {item.priority}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Axis labels */}
          <div className="flex justify-between mt-2 text-sm text-gray-600">
            <span>Low Impact</span>
            <span className="font-medium">Strategic Impact</span>
            <span>High Impact</span>
          </div>
          <div className="absolute -left-16 top-1/2 transform -translate-y-1/2 -rotate-90 text-sm text-gray-600">
            <span className="font-medium">Implementation Effort</span>
          </div>
          <div className="absolute -left-8 top-4 text-xs text-gray-500">High</div>
          <div className="absolute -left-8 bottom-4 text-xs text-gray-500">Low</div>
        </div>
        
        {/* Matrix Legend */}
        <div className="mt-6 flex justify-center">
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>Strong Alignment (≥80%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
              <span>Moderate Alignment (60-79%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span>Weak Alignment (&lt;60%)</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}